import { type DynamicImport } from "../../shared-utils/vite.preload";

export type LoaderConfig<
  T extends { [key: string]: unknown } = { [key: string]: unknown },
  R extends unknown | never = never,
> = {
  load<Name extends string, Module>(
    name: Name extends keyof T ? never : Name,
    moduleImport: DynamicImport<Module>,
    callback?: (data: T & { [K in Name]: Module }) => void
  ): LoaderConfig<T & { [K in Name]: Module }, Module>;

  set<Name extends string, Value>(
    name: Name extends keyof T ? never : Name,
    value: (data: T) => Value | Promise<Value>
  ): LoaderConfig<T & { [K in Name]: Value }, R>;

  get(): R extends never ? never : Promise<R>;
};

export class ModuleLoader<
  T extends { [key: string]: unknown } = {},
  R extends unknown | never = never,
> implements LoaderConfig<T, R>
{
  private readonly data: { [key: string]: unknown } = {};

  private readonly start: () => void;
  private sequence: Promise<unknown>;

  private readonly modulePreloads: DynamicImport<unknown>[] = [];

  // aborting stops the sequence before its next step and fails `get()` with the reason
  constructor(private readonly signal?: AbortSignal) {
    const { promise: sequence, resolve: start } = Promise.withResolvers<void>();
    this.sequence = sequence;
    this.start = start;
  }

  load<Name extends string, Module>(
    name: Name,
    moduleImport: DynamicImport<Module>,
    callback?: ((data: T & { [K in Name]: Module }) => void) | undefined
  ): LoaderConfig<T & { [K in Name]: Module }, Module> {
    this.modulePreloads.push(moduleImport);

    return this.append(name, async (_) => {
      const module = await moduleImport.load();
      this.data[name] = module;
      await callback?.(this.data as T & { [K in Name]: Module });
      return module;
    }) as LoaderConfig<T & { [K in Name]: Module }, Module>;
  }

  set<Name extends string, Value>(
    name: Name,
    fn: (data: T) => Value | Promise<Value>
  ): LoaderConfig<T & { [K in Name]: Value }, R> {
    return this.append(name, async (previous) => {
      const value = await fn(this.data as T);
      this.data[name] = value;

      // we keep the resulting value from the last `load()` definition, so that `get()` returns it
      return previous;
    }) as LoaderConfig<T & { [K in Name]: Value }, R>;
  }
  get(): never {
    this.signal?.throwIfAborted();

    if (!import.meta.env.DEV) {
      this.modulePreloads.splice(0).forEach((imp) => imp.preload());
    }
    this.start();

    // will be properly typed with LoaderConfig
    return this.sequence as never;
  }

  private append(
    name: string,
    fn: (previous: unknown) => Promise<unknown>
  ): LoaderConfig<{ [K in string]: unknown }, unknown> {
    if (Object.hasOwn(this.data, name)) {
      throw new Error(`Loader already has a value for ${name}`);
    }

    this.data[name] = null;
    this.sequence = this.sequence.then((previous) => {
      this.signal?.throwIfAborted();
      return fn(previous);
    });
    return this as unknown as LoaderConfig<{ [K in string]: unknown }, unknown>;
  }
}
